import { Routes, Route, Navigate } from "react-router";
import { AuthLayout } from "./layouts/auth-layout";
import { AuthGuard } from "./components/auth-guard";
import { ErrorBoundary } from "./components/error-boundary";
import { lazy, Suspense } from "react";

// Public pages
const LoginPage = lazy(() => import("./app/login/page"));

// Auth pages — lazy loaded for code splitting
const DashboardPage = lazy(() => import("./app/(auth)/dashboard/page"));
const CampaignsPage = lazy(() => import("./app/(auth)/campaigns/page"));
const CampaignDetailPage = lazy(() => import("./app/(auth)/campaigns/[id]/page"));
const DcoPage = lazy(() => import("./app/(auth)/dco/page"));
const AdSetsPage = lazy(() => import("./app/(auth)/adsets/page"));
const AdsPage = lazy(() => import("./app/(auth)/ads/page"));
const TrendsPage = lazy(() => import("./app/(auth)/trends/page"));
const OptimizationPage = lazy(() => import("./app/(auth)/optimization/page"));
const InsightsPage = lazy(() => import("./app/(auth)/insights/page"));
const AudienceInsightsPage = lazy(() => import("./app/(auth)/audience-insights/page"));
const AttributionPage = lazy(() => import("./app/(auth)/attribution/page"));
const CreativesPage = lazy(() => import("./app/(auth)/creatives/page"));
const CreativeLibraryPage = lazy(() => import("./app/(auth)/creative-library/page"));
const CreativesStudioPage = lazy(() => import("./app/(auth)/creatives-studio/page"));
const TemplatesPage = lazy(() => import("./app/(auth)/templates/page"));
const TemplateDetailPage = lazy(() => import("./app/(auth)/templates/[id]/page"));
const AlertsPage = lazy(() => import("./app/(auth)/alerts/page"));
const AutoActionsPage = lazy(() => import("./app/(auth)/auto-actions/page"));
const CampaignControlsPage = lazy(() => import("./app/(auth)/campaign-controls/page"));
const RulesPage = lazy(() => import("./app/(auth)/rules/page"));
const LocationsPage = lazy(() => import("./app/(auth)/locations/page"));
const GoogleAdsPage = lazy(() => import("./app/(auth)/google-ads/page"));
const LandingPagesPage = lazy(() => import("./app/(auth)/landing-pages/page"));
const ScheduledReportsPage = lazy(() => import("./app/(auth)/scheduled-reports/page"));
const TeamPage = lazy(() => import("./app/(auth)/team/page"));
const SettingsPage = lazy(() => import("./app/(auth)/settings/page"));
const WebhooksPage = lazy(() => import("./app/(auth)/webhooks/page"));
const DocsPage = lazy(() => import("./app/(auth)/docs/page"));
const AiPage = lazy(() => import("./app/(auth)/ai/page"));
const KnowledgePage = lazy(() => import("./app/(auth)/knowledge/page"));
const ApprovalsPage = lazy(() => import("./app/(auth)/approvals/page"));
const SchedulerPage = lazy(() => import("./app/(auth)/scheduler/page"));
const PlanningPage = lazy(() => import("./app/(auth)/planning/page"));
const TrackingPage = lazy(() => import("./app/(auth)/tracking/page"));
const CreativeStudioPage = lazy(() => import("./app/(auth)/creative-studio/page"));
const MidiaPagaPage = lazy(() => import("./app/(auth)/midia-paga/page"));
const MidiaPagaAnalisePage = lazy(() => import("./app/(auth)/midia-paga/analise/page"));
const StudioProPage = lazy(() => import("./app/(auth)/studio-pro/page"));
const StudioProTemplatesPage = lazy(() => import("./app/(auth)/studio-pro/templates-preview"));
const PostRapidoPage = lazy(() => import("./app/(auth)/post-rapido/page"));
const SmartCampaignPage = lazy(() => import("./app/(auth)/smart-campaign/page"));

function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center p-12">
      <div className="animate-pulse text-muted-foreground">Carregando...</div>
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Protected routes */}
        <Route element={<AuthGuard />}>
          <Route element={<AuthLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/campaigns" element={<CampaignsPage />} />
            <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
            <Route path="/dco" element={<DcoPage />} />
            <Route path="/adsets" element={<AdSetsPage />} />
            <Route path="/ads" element={<AdsPage />} />
            <Route path="/trends" element={<TrendsPage />} />
            <Route path="/optimization" element={<OptimizationPage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/audience-insights" element={<AudienceInsightsPage />} />
            <Route path="/attribution" element={<AttributionPage />} />
            <Route path="/creatives" element={<CreativesPage />} />
            <Route path="/creative-library" element={<CreativeLibraryPage />} />
            <Route path="/creatives-studio" element={<CreativesStudioPage />} />
            <Route path="/templates" element={<TemplatesPage />} />
            <Route path="/templates/:id" element={<TemplateDetailPage />} />
            <Route path="/alerts" element={<AlertsPage />} />
            <Route path="/auto-actions" element={<AutoActionsPage />} />
            <Route path="/campaign-controls" element={<CampaignControlsPage />} />
            <Route path="/rules" element={<RulesPage />} />
            <Route path="/locations" element={<LocationsPage />} />
            <Route path="/google-ads" element={<GoogleAdsPage />} />
            <Route path="/landing-pages" element={<LandingPagesPage />} />
            <Route path="/scheduled-reports" element={<ScheduledReportsPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/webhooks" element={<WebhooksPage />} />
            <Route path="/docs" element={<DocsPage />} />
            <Route path="/ai" element={<AiPage />} />
            <Route path="/knowledge" element={<KnowledgePage />} />
            <Route path="/approvals" element={<ApprovalsPage />} />
            <Route path="/scheduler" element={<SchedulerPage />} />
            <Route path="/planning" element={<PlanningPage />} />
            <Route path="/tracking" element={<TrackingPage />} />
            <Route path="/creative-studio" element={<CreativeStudioPage />} />
            <Route path="/midia-paga" element={<MidiaPagaPage />} />
            <Route path="/midia-paga/analise" element={<MidiaPagaAnalisePage />} />
            <Route path="/studio-pro" element={<StudioProPage />} />
            <Route path="/studio-pro/templates" element={<StudioProTemplatesPage />} />
            <Route path="/post-rapido" element={<PostRapidoPage />} />
            <Route path="/smart-campaign" element={<SmartCampaignPage />} />
          </Route>
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}
